/**
 * useCurrentUser Hook
 *
 * Provides current user information for access control and personalization.
 * Wraps AuthContext to provide backward-compatible interface for existing code.
 */

import { useMemo } from 'react';
import { useAuth, isITStaff } from '../contexts/AuthContext';
import type { AuthenticatedUser, ITStaffUser } from '../types/auth';
import { UserType } from '../types/auth';
import { Department } from '../types/knowledgeBase';
import { UserRole, Location } from '../types/ticket';

/**
 * Legacy CurrentUser interface for backward compatibility.
 * New code should use AuthenticatedUser from AuthContext directly.
 */
export interface CurrentUser {
  /** User ID (always use this for ticket requester/creator fields) */
  id: string;
  /** Display name */
  name: string;
  /** Email address */
  email: string;
  /** User role (role for IT staff, 'User' for end users) */
  role: UserRole;
  /** Department */
  department: Department;
  /** Physical location */
  location?: Location;
  /** Whether user has admin privileges */
  isAdmin: boolean;
  /** Whether user is IT staff */
  isITStaff: boolean;
  /** Tech ID for assignment (only for IT staff) */
  techId: string | null;
}

/**
 * Convert AuthenticatedUser to legacy CurrentUser format.
 */
function toCurrentUser(user: AuthenticatedUser): CurrentUser {
  const itStaff = isITStaff(user);
  const itUser = user as ITStaffUser;

  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: itStaff ? itUser.role : UserRole.T1_TECH, // Default role for non-IT users
    department: user.department,
    location: user.location,
    isAdmin: itStaff && itUser.role === UserRole.ADMIN,
    isITStaff: itStaff,
    techId: itStaff ? itUser.techId : null,
  };
}

/**
 * Hook to get current user information.
 * Uses AuthContext for real user data.
 *
 * @returns CurrentUser object with user information
 * @throws Error if no user is authenticated
 */
export function useCurrentUser(): CurrentUser {
  const { user, isAuthenticated } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      // Return a default user for safety during auth loading
      // This should rarely happen as AuthProvider handles initialization
      return {
        id: '',
        name: 'Loading...',
        email: '',
        role: UserRole.T1_TECH,
        department: Department.IT,
        location: undefined,
        isAdmin: false,
        isITStaff: false,
        techId: null,
      };
    }

    return toCurrentUser(user);
  }, [user, isAuthenticated]);
}

/**
 * Hook to get optional current user (returns null if not authenticated).
 * Safe to use in components that may render before auth is complete.
 */
export function useOptionalCurrentUser(): CurrentUser | null {
  const { user, isAuthenticated } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      return null;
    }
    return toCurrentUser(user);
  }, [user, isAuthenticated]);
}

/**
 * Check if user can create/edit KB articles.
 */
export function canManageArticles(role: UserRole): boolean {
  return role === UserRole.ADMIN || role === UserRole.T2_TECH;
}

/**
 * Check if user is IT staff based on role.
 */
export function isITStaffRole(role: UserRole): boolean {
  return role === UserRole.ADMIN || role === UserRole.T1_TECH || role === UserRole.T2_TECH;
}
